import { useState, useEffect } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

/**
 * Searchable single-select combobox.
 * Props:
 *  - value, onChange, options (array of strings or {value,label})
 *  - placeholder, emptyText, allLabel
 *  - allValue (value used to represent "no filter"). When selected, displays allLabel.
 *  - className for the trigger button width
 *  - disabled
 */
export const Combobox = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  emptyText = 'No results',
  allLabel,
  allValue = 'all',
  className,
  disabled = false,
  testId,
}) => {
  const [open, setOpen] = useState(false);

  // Lock background scrolling while the dropdown is open so mouse wheel
  // events don't scroll the page (only scroll the dropdown's own list).
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [open]);

  const normalized = options.map((o) => typeof o === 'string' ? { value: o, label: o } : o);
  const selectedLabel = value === allValue
    ? (allLabel || placeholder)
    : normalized.find((o) => o.value === value)?.label || value;

  const select = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('justify-between font-normal', className)}
          data-testid={testId}
        >
          <span className={value === allValue ? 'text-gray-500' : ''}>{selectedLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[220px]"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}…`} />
          <CommandList className="max-h-[320px] overscroll-contain">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allLabel && (
                <CommandItem value={allValue} onSelect={() => select(allValue)}>
                  <Check className={cn('mr-2 h-4 w-4', value === allValue ? 'opacity-100' : 'opacity-0')} />
                  <span className="text-gray-500">{allLabel}</span>
                </CommandItem>
              )}
              {normalized.map((o) => (
                <CommandItem key={o.value} value={o.label} onSelect={() => select(o.value)}>
                  <Check className={cn('mr-2 h-4 w-4', value === o.value ? 'opacity-100' : 'opacity-0')} />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default Combobox;
